<template>
  <v-layout
    id="roadmap-edit-form"
    justify-center
    wrap
  >
    <v-flex
      xs12
      md8
      lg8
    >
      <h2 v-if="!roadmap">
        {{ $t('modules.roadmap.new') }}
      </h2>
      <h2 v-else>
        {{ $t('modules.roadmap.edit') }}
      </h2>
      <v-card class="pt-7 pl-5 pr-5">
        <v-form>
          <v-layout wrap>
            <v-flex
              xs12
            >
              <v-text-field
                v-model.trim="form.name"
                :counter="100"
                :error-messages="NameErrors"
                :label="$t('modules.roadmap.name')+ '*'"
                @input="$v.form.name.$touch()"
                @blur="$v.form.name.$touch()"
              />
            </v-flex>
            <v-flex
              xs10
              md5
            >
              <v-select
                v-model.trim="form.stage_id"
                :items="moduleRoadmapStages"
                :item-text="'name'"
                :item-value="'id'"
                :error-messages="StageErrors"
                :label="$t('modules.roadmap.stage')+'*'"
              />
            </v-flex>
            <v-flex
              xs2
              md1
              pt-7
            >
              <v-btn
                small
                text
                outlined
                @click="editStageDialog = true"
              >
                <v-icon>
                  mdi-plus-circle-outline
                </v-icon>
              </v-btn>
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-menu
                v-model="datePicker"
                :close-on-content-click="false"
                :nudge-right="120"
                top
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="formatedValidTillDate"
                    :label="$t('modules.roadmap.eventDate')+'*'"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-on="on"
                    @input="$v.form.event_date.$touch()"
                    @blur="$v.form.event_date.$touch()"
                  />
                </template>
                <v-date-picker 
                  v-model="form.event_date" 
                  :locale="$i18n.locale"
                  :min="$moment().format('YYYY-MM-DD')"
                  @input="datePicker = false"
                />
              </v-menu>
            </v-flex>
            <v-flex
              xs12
              mt-5
            >
              <div
                v-for="(field, i) in schema"
                :key="i"
                class="mt-3"
              >
                <NestedField
                  :ref="field.key"
                  :field="field"
                  :values="schemaValues"
                />
              </div>
            </v-flex>
            <v-flex
              xs12
              mt-3
            >
              <b>{{ $t("modules.roadmap.description") }}</b>
            </v-flex>
            <v-flex>
              <vue-editor
                id="editor"
                v-model="form.description"
                :editor-toolbar="customToolbar"
                :editor-options="editorSettings"
              />
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex
              xs12
              md12
            >
              <v-btn
                fab
                outlined
                small
                text
                @click="$emit('closeEditMode', false)"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-btn
                color="primary"
                style="float:right"
                :disabled="loading || ($v.$invalid)"
                :loading="loading"
                @click="save()"
              >
                {{ $t('globals.saveButton') }}
              </v-btn>
            </v-flex>
          </v-layout>
        </v-form>
      </v-card>
    </v-flex>
    <edit-stage-dialog
      v-if="editStageDialog"
      :show-dialog="editStageDialog"
      @closeEditStageDialog="closeEditStageDialog"
    />
  </v-layout>
</template>

<script>
import ROADMAP_STAGES_QUERY from "@/graphql/queries/modules/RoadmapStages.gql";
import ROADMAP_CONFIG_SCHEMA_QUERY from "@/graphql/queries/modules/RoadmapConfig.gql";
import ROADMAP_CREATE_MUTATION from "@/graphql/mutations/modules/RoadmapCreate.gql"; 
import ROADMAP_UPDATE_MUTATION from "@/graphql/mutations/modules/RoadmapUpdate.gql";
import EditStageDialog from "./partial/EditStageDialog";
import NestedField from "@/components/core/NestedField.vue";

import { validationMixin } from "vuelidate";
import {
  required,
  minLength,
  maxLength
} from "vuelidate/lib/validators";
import { VueEditor } from "vue2-editor";

export default {
  name: "ModuleRoadmapEditForm",
  components: {
    VueEditor,
    EditStageDialog,
    NestedField
  },
  mixins: [validationMixin],
  props: {
    roadmap: {
      type: Object,
      default: null
    },
    whitelabelUuid: {
      type: String,
      default: null
    },
  },
  data() {
    return {
      loading: false,
      editStageDialog: false,
      datePicker: null,
      validatedSchema: true,
      customToolbar: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': []}, 'link', 'image'],

        ['clean']
      ],
      editorSettings: {
        modules: {
          imageResize: {
            displaySize: true,
            imageDrop: true
          }
        }
      },
      form: {}
    }
  },
  computed: {
    formatedValidTillDate() {
      return this.$moment(this.form.event_date).format('ll');
    },
    rules() {
      return {
        name: {
          required,
          minLength: minLength(3),
          maxLength: maxLength(100),
        },
        stage_id: {
         required,
        },
        event_date: {
         required,
        },
      }
    },
    NameErrors() {
      const errors = [];
      if (!this.$v.form.name.$dirty) return errors;
      !this.$v.form.name.required && errors.push(this.$t("globals.reqField"));
      !this.$v.form.name.minLength &&
        errors.push(this.$t("globals.minError", {char: this.$v.form.name.$params.minLength.min}));
      !this.$v.form.name.maxLength &&
        errors.push(this.$t("globals.maxError", {char: this.$v.form.name.$params.maxLength.max}));
      return errors;
    },
    StageErrors() {
      const errors = [];
      if (!this.$v.form.stage_id.$dirty) return errors;
      !this.$v.form.stage_id.required && errors.push(this.$t("globals.reqField"));
      return errors;
    },
    EventDateErrors() {
      const errors = [];
      if (!this.$v.form.event_date.$dirty) return errors;
      !this.$v.form.event_date.required && errors.push(this.$t("globals.reqField"));
      return errors;
    },
    schema() {
      return this.moduleRoadmapConfig
        ? JSON.parse(this.moduleRoadmapConfig.custom_config_schema)
        : {};
    },
    schemaValues() {
      return this.form && this.form.custom_values
        ? JSON.parse(this.form.custom_values)
        : {};
    },
  },
  mounted() {
    const form = JSON.parse(JSON.stringify(this.roadmap));
    if (form) {
      this.form = {
        roadmap_uuid: form.uuid,
        name: form.name,
        stage_id: form.stage.id,
        event_date: this.$moment(form.event_date).format('YYYY-MM-DD'),
        description: form.description,
        custom_values: form.custom_values
      }
    } else {
      this.form = {
        name: "",
        stage_id: "",
        event_date: this.$moment().add(1, 'days').format('YYYY-MM-DD'),
        description: "",
        custom_values: "{}"
      }
    }
  },
  validations() {
    return {
      form: this.rules
    };
  },
  methods: {
    closeEditStageDialog(val) {
      this.editStageDialog = false
      if (val) {
        this.$parent.queries.moduleRoadmapStages.refetch()
      }
    },
    validateField(field) {
      if (field.$v !== undefined) {
        field.$v.$touch();
        if (field.$v.$invalid) {
          this.validatedSchema = false;
          return -1;
        }
      }
    },
    checkArrayFields(fields, refs) {
      return fields.reduce((config, field) => {
        refs[field.key].forEach((ref, ind) => {
          if (!config[ind]) {
            config[ind] = {};
          }
          config[ind][field.key] = this.checkFields(
            field,
            ref.$refs[field.key]
          );
        });
        return config;
      }, []);
    },
    checkFields(schema, refs, config = {}) {
      const isArrayItem = (item) => {
        return item.field === "object" && item.children;
      };
      const isEmptyCheckbox = (item, field) => {
        return item.field === "checkbox" && field[item.key] == null;
      };
      const getSimpleProperty = (propertyId, label, field) => {
        return {
          value: parseFloat(field[propertyId]) || field[propertyId],
          label
        };
      };
      if (Array.isArray(schema)) {
        schema.forEach((field) => {
          if (isArrayItem(field)) {
            config[field.key] = this.checkArrayFields(
              field.children,
              refs[field.key][0].$refs
            );
          } else {
            config[field.key] = this.checkFields(
              field,
              refs[field.key][0].$refs[field.key],
              config
            );
          }
        });
      } else {
        if (isEmptyCheckbox(schema, refs)) {
          refs[schema.key] = false;
        }
        this.validateField(refs);
        return getSimpleProperty(schema.key, schema.name, refs);
      }
      return config;
    },
    async save() {
      this.validatedSchema = true
      this.$v.$touch();
      if (this.$v.$invalid) {
        return
      }

      this.form.custom_values = await JSON.stringify(
        this.checkFields(this.schema, this.$refs)
      );

      if(!this.validatedSchema) {
        return
      }

      this.loading = true;
      this.form.whitelabel_uuid = this.whitelabelUuid

      this.$apollo
        .mutate({
          mutation: !this.roadmap ? ROADMAP_CREATE_MUTATION : ROADMAP_UPDATE_MUTATION,
          variables: this.form
        })
        .then(data => {
          this.$emit('closeEditMode', true)
        })
        .catch(error => {
          this.errorHandler(error);
          this.loading = false;
        });
    }
  },
  apollo: {
    moduleRoadmapStages: {
      fetchPolicy: "network-only",
      query() {
        return ROADMAP_STAGES_QUERY;
      },
      variables() {
        return {
          whitelabel_uuid: this.whitelabelUuid,
        }
      },
      skip() {
        return !this.whitelabelUuid;
      },
    },
    moduleRoadmapConfig: {
      fetchPolicy: "network-only",
      query() {
        return ROADMAP_CONFIG_SCHEMA_QUERY;
      },
      variables() {
        return {
          whitelabel_uuid: this.whitelabelUuid,
        };
      },
      skip() {
        return !this.whitelabelUuid;
      },
    },
  },
}
</script>

<style lang="scss">
#editor {
  height: 400px;
}
#roadmap-edit-form {
  .image {
    width: 306px;
    margin: 0 auto;
    border: 3px solid;
    border-radius: 5px;
    text-align: center;
    cursor: pointer;
    i {
      margin: 5px 0 0px;
      font-size: 26px;
    }
    p {
      margin: 7px 0 0!important;
      &:first-of-type {
        font-weight: 700;
      }
      line-height: 0.85rem;
    }
    small {
      font-size: 11px;
    }
    &.no-border {
      border: 0 !important;
    }
  }
}
</style>
